<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const loading = ref(false);
const productName = ref('');
const productDescription = ref('');
const productValue = ref('');
const productType = ref('product');
const productColor = ref('#1f2937');
const productCurrency = ref('BRL');

const typeOptions = [
  { label: 'Produto', value: 'product' },
  { label: 'Serviço', value: 'service' },
];

const currencyOptions = [
  { label: 'Real (R$)', value: 'BRL' },
  { label: 'Dólar ($)', value: 'USD' },
  { label: 'Euro (€)', value: 'EUR' },
];

const colorOptions = [
  '#1f2937',
  '#dc2626',
  '#ea580c',
  '#ca8a04',
  '#16a34a',
  '#0891b2',
  '#2563eb',
  '#7c3aed',
  '#c2410c',
  '#be123c',
];

const onClose = () => {
  emit('close');
};

const addProduct = async () => {
  if (!productName.value.trim()) {
    useAlert(t('PRODUCT_MGMT.ADD.FORM.NAME.ERROR'));
    return;
  }

  if (!productValue.value || parseFloat(productValue.value) <= 0) {
    useAlert(t('PRODUCT_MGMT.ADD.FORM.VALUE.ERROR'));
    return;
  }

  loading.value = true;
  try {
    await store.dispatch('products/create', {
      name: productName.value.trim(),
      description: productDescription.value.trim(),
      base_price: parseFloat(productValue.value),
      value: parseFloat(productValue.value), // Manter compatibilidade
      product_type: productType.value,
      color: productColor.value,
      currency: productCurrency.value,
    });

    useAlert(t('PRODUCT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    const errorMessage =
      error?.message || t('PRODUCT_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <Modal :show="true" @close="onClose">
    <div class="p-6">
      <h2 class="text-lg font-medium text-n-slate-12 mb-4">
        {{ $t('PRODUCT_MGMT.ADD.TITLE') }}
      </h2>

      <form class="space-y-4" @submit.prevent="addProduct">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('PRODUCT_MGMT.ADD.FORM.NAME.LABEL') }}
          </label>
          <input
            v-model="productName"
            type="text"
            class="w-full px-3 py-2 border border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            :placeholder="$t('PRODUCT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            required
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('PRODUCT_MGMT.ADD.FORM.DESCRIPTION.LABEL') }}
          </label>
          <textarea
            v-model="productDescription"
            rows="3"
            class="w-full px-3 py-2 border border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            :placeholder="$t('PRODUCT_MGMT.ADD.FORM.DESCRIPTION.PLACEHOLDER')"
          />
        </div>

        <div class="grid grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('PRODUCT_MGMT.ADD.FORM.VALUE.LABEL') }}
            </label>
            <input
              v-model="productValue"
              type="number"
              step="0.01"
              min="0"
              class="w-full px-3 py-2 border border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              :placeholder="$t('PRODUCT_MGMT.ADD.FORM.VALUE.PLACEHOLDER')"
              required
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              Moeda
            </label>
            <select
              v-model="productCurrency"
              class="w-full px-3 py-2 border border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option
                v-for="option in currencyOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('PRODUCT_MGMT.ADD.FORM.TYPE.LABEL') }}
            </label>
            <select
              v-model="productType"
              class="w-full px-3 py-2 border border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option
                v-for="option in typeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('PRODUCT_MGMT.ADD.FORM.COLOR.LABEL') }}
          </label>
          <div class="flex gap-2 flex-wrap">
            <button
              v-for="color in colorOptions"
              :key="color"
              type="button"
              class="w-8 h-8 rounded-full border-2 transition-all"
              :class="
                productColor === color
                  ? 'border-n-slate-12 scale-110'
                  : 'border-n-weak hover:scale-105'
              "
              :style="{ backgroundColor: color }"
              @click="productColor = color"
            />
          </div>
        </div>
      </form>

      <div class="flex justify-end gap-2 mt-6">
        <Button
          :label="$t('PRODUCT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          slate
          @click="onClose"
        />
        <Button
          :label="$t('PRODUCT_MGMT.ADD.CREATE_BUTTON_TEXT')"
          :loading="loading"
          @click="addProduct"
        />
      </div>
    </div>
  </Modal>
</template>
